import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../providers/AuthProvider';
import axios from 'axios';

// Import onboarding steps (All Premium refactored - Epic 1.3 Final)
import WelcomeStep from '../components/onboarding/WelcomeStep.jsx';
import RegionStep from '../components/onboarding/RegionStep.jsx';
import LanguageStep from '../components/onboarding/LanguageStep.jsx';
import CountryStep from '../components/onboarding/CountryStep.jsx';
import TimezoneStep from '../components/onboarding/TimezoneStep.jsx';
import CompanyStep from '../components/onboarding/CompanyStep.jsx';
import SocialNetworksStep from '../components/onboarding/SocialNetworksStep.jsx';
import GoalsStep from '../components/onboarding/GoalsStep.jsx';
import PreferencesStep from '../components/onboarding/PreferencesStep.jsx';
import CompleteStep from '../components/onboarding/CompleteStep.jsx';
import ProgressBar from '../components/onboarding/ProgressBar';

const TOTAL_STEPS = 10;

export default function OnboardingPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { t } = useTranslation(['onboarding', 'common']);
  
  const [currentStep, setCurrentStep] = useState(0);
  const [onboardingData, setOnboardingData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  
  const API_URL = process.env.REACT_APP_BACKEND_URL;

  // Load saved progress on mount
  useEffect(() => {
    const loadProgress = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/onboarding/progress`, {
          withCredentials: true
        });
        
        if (response.data.current_step) {
          setCurrentStep(response.data.current_step);
          setOnboardingData(response.data.data || {});
        }
      } catch (error) {
        console.error('Error loading onboarding progress:', error);
      } finally {
        setIsLoading(false);
      }
    };

    loadProgress();
  }, [API_URL]);

  // Auto-save progress when data changes
  const saveProgress = async (step, data) => {
    try {
      await axios.post(
        `${API_URL}/api/onboarding/save`,
        { step, data },
        { withCredentials: true }
      );
    } catch (error) {
      console.error('Error saving progress:', error);
    }
  };

  const handleStepData = (stepData) => {
    const updatedData = { ...onboardingData, ...stepData };
    setOnboardingData(updatedData);
    saveProgress(currentStep, updatedData);
  };

  const nextStep = () => {
    if (currentStep < TOTAL_STEPS - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const completeOnboarding = async () => {
    try {
      await axios.post(
        `${API_URL}/api/onboarding/complete`,
        onboardingData,
        { withCredentials: true }
      );
      navigate('/app/dashboard');
    } catch (error) {
      console.error('Error completing onboarding:', error);
    }
  };

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-text-secondary">{t('common:loading')}</p>
        </div>
      </div>
    );
  }

  // Render current step
  const renderStep = () => {
    const stepProps = {
      data: onboardingData,
      onNext: nextStep,
      onBack: prevStep,
      onData: handleStepData,
      currentStep,
      totalSteps: TOTAL_STEPS
    };

    switch (currentStep) {
      case 0:
        return <WelcomeStep {...stepProps} />;
      case 1:
        return <RegionStep {...stepProps} />;
      case 2:
        return <LanguageStep {...stepProps} />;
      case 3:
        return <CountryStep {...stepProps} />;
      case 4:
        return <TimezoneStep {...stepProps} />;
      case 5:
        return <CompanyStep {...stepProps} />;
      case 6:
        return <SocialNetworksStep {...stepProps} />;
      case 7:
        return <GoalsStep {...stepProps} />;
      case 8:
        return <PreferencesStep {...stepProps} />;
      case 9:
        return <CompleteStep {...stepProps} onComplete={completeOnboarding} />;
      default:
        return <WelcomeStep {...stepProps} />;
    }
  };

  return (
    <div className="min-h-screen bg-background bg-noise">
      {/* Progress Bar (hidden on welcome and complete screens) */}
      {currentStep > 0 && currentStep < TOTAL_STEPS - 1 && (
        <ProgressBar
          currentStep={currentStep}
          totalSteps={TOTAL_STEPS}
          onBack={prevStep}
        />
      )}
      
      {/* Add padding-top when progress bar is visible */}
      <div className={currentStep > 0 && currentStep < TOTAL_STEPS - 1 ? 'pt-20' : ''}>
        {renderStep()}
      </div>
    </div>
  );
}
